import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const DOC_TYPES = [
  { value: 'CV',                    label: 'CV / Portfolio',               icon: '📄', category: 'pro' },
  { value: 'DIPLOME',               label: 'Diplôme',                      icon: '🎓', category: 'pro' },
  { value: 'CERTIFICATION',         label: 'Certification',                icon: '🏅', category: 'pro' },
  { value: 'ATTESTATION_COMPETENCES', label: 'Attestation de compétences', icon: '📋', category: 'pro' },
  { value: 'RECYCLAGE_MAC',         label: 'Recyclage / MAC',              icon: '🔄', category: 'pro' },
  { value: 'RC_PRO',                label: 'RC Pro',                       icon: '🛡️', category: 'admin' },
  { value: 'RIB',                   label: 'RIB',                          icon: '🏦', category: 'admin' },
  { value: 'URSSAF',                label: 'Attestation URSSAF',           icon: '📑', category: 'admin' },
  { value: 'KBIS',                  label: 'Kbis / Justificatif société',  icon: '🏢', category: 'admin' },
  { value: 'IDENTITE',              label: "Pièce d'identité",             icon: '🪪', category: 'admin' },
  { value: 'CARTE_PRO',             label: 'Carte professionnelle',        icon: '💼', category: 'admin' },
  { value: 'AUTRE',                 label: 'Autre document',               icon: '📎', category: 'other' },
]

const TYPE_MAP = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t]))

interface RequiredItem {
  key: string
  label: string
  icon: string
  type: 'doc' | 'profile'
  docType?: string
  conditional?: string
}

const REQUIRED_ITEMS: RequiredItem[] = [
  { key: 'SIRET',     label: 'Numéro SIRET',             icon: '🔢', type: 'profile' },
  { key: 'RC_PRO',    label: 'RC Pro',                   icon: '🛡️', type: 'doc', docType: 'RC_PRO' },
  { key: 'RIB',       label: 'RIB bancaire',             icon: '🏦', type: 'doc', docType: 'RIB' },
  { key: 'URSSAF',    label: 'Attestation URSSAF',       icon: '📑', type: 'doc', docType: 'URSSAF' },
  { key: 'KBIS',      label: 'Kbis (si société)',        icon: '🏢', type: 'doc', docType: 'KBIS', conditional: 'société' },
  { key: 'CV',        label: 'CV / Portfolio',           icon: '📄', type: 'doc', docType: 'CV' },
  { key: 'DIPLOME',   label: 'Diplôme(s)',               icon: '🎓', type: 'doc', docType: 'DIPLOME' },
  { key: 'CERTIFICATION', label: 'Certification ou attestation de compétences', icon: '🏅', type: 'doc', docType: 'CERTIFICATION' },
  { key: 'RECYCLAGE_MAC', label: 'Recyclage / MAC à jour', icon: '🔄', type: 'doc', docType: 'RECYCLAGE_MAC' },
]

interface Doc {
  id: string
  type: string
  name: string
  filename: string
  size: number
  scanStatus: 'PENDING' | 'SCANNING' | 'DONE' | 'FAILED'
  expiresAt: string | null
  scanData: { summary?: string; siret?: string; iban?: string } | null
  createdAt: string
}

interface Completion {
  isComplete: boolean
  missingItems: string[]
  uploadedTypes: string[]
  siret: string | null
  isSociete: boolean
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
}

function expiryBadge(expiresAt: string | null) {
  if (!expiresAt) return null
  const d = new Date(expiresAt)
  const now = new Date()
  const daysLeft = Math.floor((d.getTime() - now.getTime()) / 86400000)
  const label = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
  if (daysLeft < 0)  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-danger/15 text-danger border border-danger/25">❌ Expiré le {label}</span>
  if (daysLeft < 30) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/25">⚠️ Expire le {label}</span>
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-success/10 text-success border border-success/20">✓ Valide jusqu'au {label}</span>
}

function scanBadge(status: Doc['scanStatus']) {
  if (status === 'SCANNING') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent/10 text-accent border border-accent/20"><span className="w-2 h-2 rounded-full bg-accent animate-pulse" />Analyse…</span>
  if (status === 'FAILED')   return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/15 text-red-400 border border-red-500/30">Scan échoué</span>
  if (status === 'DONE')     return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-500/15 text-green-400 border border-green-500/30">Scan réussi</span>
  return null
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function QualificationsPage() {
  const [docs, setDocs] = useState<Doc[]>([])
  const [completion, setCompletion] = useState<Completion | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [selectedType, setSelectedType] = useState('CV')
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem('velink_token')}` }
  }

  async function fetchAll() {
    try {
      const [docsRes, completionRes] = await Promise.all([
        fetch(`${API}/api/providers/me/documents`, { headers: authHeaders() }),
        fetch(`${API}/api/providers/me/completion`, { headers: authHeaders() }),
      ])
      if (docsRes.ok) setDocs(await docsRes.json())
      if (completionRes.ok) setCompletion(await completionRes.json())
    } catch { /* silencieux */ }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchAll() }, [])

  async function uploadFile(file: File) {
    if (uploading) return
    setError(''); setSuccess('')
    setUploading(true)
    const fd = new FormData()
    fd.append('type', selectedType)
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/providers/me/documents`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message ?? 'Erreur upload')
      setDocs((prev) => [data, ...prev])
      setSuccess('Document ajouté avec succès')
      if (fileRef.current) fileRef.current.value = ''
      const cr = await fetch(`${API}/api/providers/me/completion`, { headers: authHeaders() })
      if (cr.ok) setCompletion(await cr.json())
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'upload")
    } finally {
      setUploading(false)
    }
  }

  async function deleteDoc(id: string) {
    try {
      await fetch(`${API}/api/providers/me/documents/${id}`, { method: 'DELETE', headers: authHeaders() })
      setDocs((prev) => prev.filter((d) => d.id !== id))
      const cr = await fetch(`${API}/api/providers/me/completion`, { headers: authHeaders() })
      if (cr.ok) setCompletion(await cr.json())
    } catch { setError('Erreur lors de la suppression') }
  }

  async function fetchBlob(doc: Doc): Promise<Blob> {
    const [providerId, filename] = doc.filename.split('/')
    const url = `${API}/api/providers/me/documents/file/${providerId}/${filename}`
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` } })
    if (!res.ok) throw new Error()
    return res.blob()
  }

  async function openDoc(doc: Doc) {
    try {
      const blob = await fetchBlob(doc)
      const objectUrl = URL.createObjectURL(blob)
      window.open(objectUrl, '_blank')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
    } catch {
      const [providerId, filename] = doc.filename.split('/')
      window.open(`${API}/api/providers/me/documents/file/${providerId}/${filename}`, '_blank')
    }
  }

  async function downloadDoc(doc: Doc) {
    try {
      const blob = await fetchBlob(doc)
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = doc.name
      a.click()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
    } catch {
      setError('Erreur lors du téléchargement')
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) uploadFile(file)
  }

  const missing = completion?.missingItems ?? []

  // Retourne true uniquement si le type est manquant ET effectivement requis (pas conditionnel non-applicable)
  function isMissingRequired(docType: string) {
    if (!missing.includes(docType)) return false
    if (docType === 'KBIS' && !completion?.isSociete) return false
    return true
  }

  const doneCount = REQUIRED_ITEMS.filter((item) => {
    if (item.conditional === 'société' && !completion?.isSociete) return false
    return !missing.includes(item.key)
  }).length
  const totalRequired = REQUIRED_ITEMS.filter(
    (item) => item.conditional !== 'société' || completion?.isSociete
  ).length
  const percent = totalRequired > 0 ? Math.round((doneCount / totalRequired) * 100) : 0

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Qualifications</h1>
        <p className="text-text-secondary mt-1">
          Ajoutez vos diplômes, certifications et documents professionnels.
        </p>
      </div>

      {/* Barre de complétion */}
      {!loading && completion && (
        <div className={[
          'glass-card p-5 border',
          completion.isComplete
            ? 'border-success/30 bg-success/5'
            : 'border-warning/30 bg-warning/5',
        ].join(' ')}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">{completion.isComplete ? '✅' : '⚠️'}</span>
              <span className={`font-semibold text-sm ${completion.isComplete ? 'text-success' : 'text-[#F59E0B]'}`}>
                {completion.isComplete ? 'Profil complet' : 'Profil incomplet'}
              </span>
            </div>
            <span className="text-text-muted text-xs">{doneCount}/{totalRequired} éléments</span>
          </div>
          <div className="w-full h-2 rounded-full bg-white/10">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${completion.isComplete ? 'bg-success' : 'bg-[#F59E0B]'}`}
              style={{ width: `${percent}%` }}
            />
          </div>

          {/* Checklist */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {REQUIRED_ITEMS.map((item) => {
              const isConditional = item.conditional === 'société' && !completion.isSociete
              const isDone = isConditional || !missing.includes(item.key)
              return (
                <div
                  key={item.key}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                    isConditional
                      ? 'text-text-muted opacity-50'
                      : isDone
                        ? 'text-success'
                        : 'text-[#F59E0B]'
                  }`}
                >
                  <span className="flex-shrink-0 w-4 text-center">
                    {isConditional ? '—' : isDone ? '✓' : '○'}
                  </span>
                  <span>{item.icon} {item.label}</span>
                  {item.type === 'profile' && !isDone && (
                    <Link to="/profil-prestataire" className="ml-auto text-accent underline">
                      Renseigner →
                    </Link>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Zone d'upload */}
      <div className="glass-card p-6">
        <h2 className="text-base font-semibold text-text-primary mb-4">Ajouter un document</h2>

        {/* Type */}
        <div className="mb-4">
          <label className="text-sm text-text-secondary mb-2 block">Type de document</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {DOC_TYPES.filter((t) => t.value !== 'AUTRE').map((t) => (
              <button
                key={t.value}
                onClick={() => setSelectedType(t.value)}
                className={[
                  'flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all text-left',
                  selectedType === t.value
                    ? 'bg-accent/15 border-accent/40 text-accent'
                    : isMissingRequired(t.value)
                      ? 'border-[#F59E0B]/30 text-[#F59E0B]/80 hover:border-[#F59E0B]/60 hover:text-[#F59E0B]'
                      : 'border-white/10 text-text-secondary hover:border-white/20 hover:text-text-primary',
                ].join(' ')}
              >
                <span>{t.icon}</span>
                <span className="leading-tight">{t.label}</span>
                {isMissingRequired(t.value) && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#F59E0B] flex-shrink-0" />
                )}
              </button>
            ))}
            <button
              onClick={() => setSelectedType('AUTRE')}
              className={[
                'flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all text-left',
                selectedType === 'AUTRE'
                  ? 'bg-accent/15 border-accent/40 text-accent'
                  : 'border-white/10 text-text-secondary hover:border-white/20 hover:text-text-primary',
              ].join(' ')}
            >
              <span>📎</span>
              <span>Autre document</span>
            </button>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={[
            'relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
            dragOver
              ? 'border-accent/60 bg-accent/10'
              : 'border-white/15 hover:border-white/30 hover:bg-white/[0.03]',
          ].join(' ')}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f) }}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
              <p className="text-text-secondary text-sm">Upload en cours…</p>
            </div>
          ) : (
            <>
              <div className="text-4xl mb-3">📤</div>
              <p className="text-text-primary text-sm font-medium">
                Glissez un fichier ici ou <span className="text-accent">parcourir</span>
              </p>
              <p className="text-text-muted text-xs mt-1">PDF, JPG, PNG — max 10 Mo</p>
            </>
          )}
        </div>

        {error && (
          <div className="mt-3 px-4 py-2.5 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-3 px-4 py-2.5 rounded-xl bg-success/10 border border-success/25 text-success text-sm">
            {success}
          </div>
        )}
      </div>

      {/* Liste des documents */}
      <div className="glass-card p-6">
        <h2 className="text-base font-semibold text-text-primary mb-4">
          Mes documents
          {docs.length > 0 && (
            <span className="ml-2 text-xs font-normal text-text-muted px-2 py-0.5 rounded-full bg-white/[0.08]">
              {docs.length}
            </span>
          )}
        </h2>

        {loading ? (
          <div className="text-text-muted text-sm animate-pulse py-4 text-center">Chargement…</div>
        ) : docs.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-4xl mb-3 opacity-40">📂</div>
            <p className="text-text-secondary text-sm">Aucun document ajouté</p>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((doc) => {
              const meta = TYPE_MAP[doc.type]
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.07] hover:bg-white/[0.07] transition-colors group"
                >
                  <span className="text-xl flex-shrink-0">{meta?.icon ?? '📎'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary text-sm font-medium truncate">{doc.name}</p>
                    <p className="text-text-muted text-xs mt-0.5">
                      {meta?.label ?? doc.type}
                      {' · '}{formatSize(doc.size)}
                      {' · '}{formatDate(doc.createdAt)}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {expiryBadge(doc.expiresAt)}
                      {scanBadge(doc.scanStatus)}
                      {doc.scanData?.summary && doc.scanStatus === 'DONE' && !doc.expiresAt && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] text-text-muted border border-white/10 bg-white/5">
                          {doc.scanData.summary}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openDoc(doc)}
                      title="Voir"
                      className="p-1.5 rounded-lg hover:bg-white/[0.08] text-text-secondary hover:text-text-primary transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => downloadDoc(doc)}
                      title="Télécharger"
                      className="p-1.5 rounded-lg hover:bg-white/[0.08] text-text-secondary hover:text-text-primary transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => deleteDoc(doc.id)}
                      title="Supprimer"
                      className="p-1.5 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
